import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Suporte from './components/pages/Suporte';
import Container from './components/layout/Container';
import Navbar from './components/layout/Navbar';
import CadastroUsuario from './components/pages/CadastroUsuario';
import Login from './components/pages/Login';
import { jwtDecode } from 'jwt-decode';
import Home from './components/pages/Home';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import PerfilUsuario from './components/pages/PerfilUsuario';
import flaskServer from './components/config/flaskserver';
import axios from 'axios';
import RelatorioComponente from './components/pages/RelatorioComponente';
import ResetPassword from './components/pages/ResetPassword';
import LogViewer from './components/pages/LogViewer';
import TabelaPreview from './components/layout/TabelaPreview';
import ConciliacaoTiss from './components/pages/ConciliacaoTiss';
import CadastrosFaturamento from './components/pages/CadastrosFaturamento';
import DicionarioIA from './components/pages/DicionarioIA';
import AuditoriaTissDashboard from './components/pages/AuditoriaTissDashboard';
import RegrasAuditoria from './components/pages/RegrasAuditoria';
import ComparadorProcedimentos from './components/pages/ComparadorProcedimentos';
import PivotViewPage from './components/pages/PivotViewPage';
import CustomAudit from './components/pages/CustomAudit';
import AuditoriaSus from './components/pages/AuditoriaSus';
import { Box, Typography } from '@mui/material';

/**
 * AcessoNegado – shown when a user navigates to a module they don't have access to.
 */
const AcessoNegado = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 10 }}>
        <Typography variant="h4" color="error" gutterBottom>Acesso Negado</Typography>
        <Typography variant="body1" color="text.secondary">
            Você não tem permissão para acessar este módulo. Entre em contato com o administrador.
        </Typography>
    </Box>
);

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [userModulos, setUserModulos] = useState([]);   // ← modules from JWT
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [nome_empresa, setNome_empresa] = useState('');
    const [open, setOpen] = useState(true);

    const handleDrawerOpen = () => setOpen(true);
    const handleDrawerClose = () => setOpen(false);


    const handleLogout = async () => {
        const token = localStorage.getItem('token');
        localStorage.removeItem('token');
        if (!token) return;
        try {
            const response = await axios.post(
                `${flaskServer.flaskServerIP}/logout`, {},
                { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            if (response.data.message === 'Logout bem-sucedido') {
                localStorage.removeItem('token');
                setIsLoggedIn(false);
                setIsAdmin(false);
                setUserModulos([]);
            }
        } catch (error) { }
        window.location.reload();
    };

    const checkStoredToken = () => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            try {
                const decodedToken = jwtDecode(storedToken);
                const tokenExpirationTime = decodedToken.exp * 1000;
                if (tokenExpirationTime > Date.now()) {
                    setIsLoggedIn(true);
                    setIsAdmin(decodedToken.is_admin);
                    setUserModulos(decodedToken.modulos || []);
                    setUsername(decodedToken.username);
                    setEmail(decodedToken.email);
                    setNome_empresa(decodedToken.nome_empresa);
                    setToken(storedToken);
                } else {
                    localStorage.removeItem('token');
                    handleLogout();
                }
            } catch (error) {
                localStorage.removeItem('token');
                handleLogout();
            }
        } else {
            setIsLoggedIn(false);
            // handleLogout(); // Removed to avoid infinite loop on mount when not logged in
        }
    };

    useEffect(() => {
        checkStoredToken();
    }, []);

    useEffect(() => {
        const checkFlaskServer = async () => {
            try {
                console.log("--- PROXY MODE ACTIVE ---");
                console.log(`Attempting to connect to Flask server at: ${flaskServer.flaskServerIPRoot}`);
                const config = { timeout: 10000, headers: { 'Content-Type': 'application/json' } };
                const response = await axios.get(`${flaskServer.flaskServerIPRoot}/`, config);
                if (response.status === 200) console.log("Flask server is active");
            } catch (error) {
                console.error("Error checking Flask server:", error);
            }
        };
        checkFlaskServer();
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
                <AppContent
                    isLoggedIn={isLoggedIn}
                    isAdmin={isAdmin}
                    userModulos={userModulos}
                    username={username}
                    email={email}
                    nome_empresa={nome_empresa}
                    open={open}
                    handleDrawerOpen={handleDrawerOpen}
                    handleDrawerClose={handleDrawerClose}
                    handleLogout={handleLogout}
                    setToken={setToken}
                    setIsLoggedIn={setIsLoggedIn}
                    handleLogin={checkStoredToken}
                />
            </Router>
        </ThemeProvider>
    );
}

const AppContent = ({
    isLoggedIn, isAdmin, userModulos, username, email, nome_empresa,
    open, handleDrawerOpen, handleDrawerClose, handleLogout, setToken, setIsLoggedIn, handleLogin
}) => {
    const location = useLocation();
    const isPivotPage = location.pathname.startsWith('/auditoria/pivot');

    /**
     * Returns true if admin OR modulos includes the key.
     */
    const hasAccess = (modulo) => isAdmin || userModulos.includes(modulo);

    /** Helper: render the page only if user has access to the given module. */
    const ProtectedPage = ({ modulo, children }) => {
        if (!isLoggedIn) return <Login setToken={setToken} handleLogin={handleLogin} />;
        if (!hasAccess(modulo)) return <AcessoNegado />;
        return children;
    };

    const LoginOrPage = ({ children }) =>
        isLoggedIn ? children : <Login setToken={setToken} handleLogin={handleLogin} />;

    return (
        <Box sx={{ display: 'flex' }}>
            {isLoggedIn && (
                <Navbar
                    handleLogout={handleLogout}
                    isAdmin={isAdmin}
                    userModulos={userModulos}
                    username={username}
                    email={email}
                    nome_empresa={nome_empresa}
                    open={open}
                    handleDrawerOpen={handleDrawerOpen}
                    handleDrawerClose={handleDrawerClose}
                />
            )}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: isPivotPage ? 0 : 3,
                    width: { sm: `calc(100% - ${open ? 260 : 65}px)` },
                    ml: isLoggedIn ? `${open ? 0 : 0}px` : 0,
                    transition: (theme) => theme.transitions.create(['width', 'margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                }}
            >
                <Container customClass="min-height" fluid={isPivotPage}>
                    <Routes>
                        {/* Rotas públicas */}
                        <Route path="/reset-password/:token" element={<ResetPassword />} />

                        {/* Home – sempre acessível quando logado */}
                        <Route path="/" element={<LoginOrPage><Home /></LoginOrPage>} />

                        {/* Conciliar */}

                        {/* TISS */}
                        <Route path="/conciliacao-tiss" element={
                            <ProtectedPage modulo="cadastros_tiss"><ConciliacaoTiss handleLogout={handleLogout} /></ProtectedPage>
                        } />
                        <Route path="/cadastros-tiss" element={
                            <ProtectedPage modulo="cadastros_tiss"><CadastrosFaturamento handleLogout={handleLogout} /></ProtectedPage>
                        } />
                        <Route path="/auditoria-tiss" element={
                            <ProtectedPage modulo="cadastros_tiss"><AuditoriaTissDashboard handleLogout={handleLogout} /></ProtectedPage>
                        } />
                        <Route path="/auditoria-custom" element={
                            <ProtectedPage modulo="cadastros_tiss"><CustomAudit handleLogout={handleLogout} /></ProtectedPage>
                        } />
                        <Route path="/regras-auditoria" element={
                            <ProtectedPage modulo="cadastros_tiss"><RegrasAuditoria handleLogout={handleLogout} /></ProtectedPage>
                        } />
                        <Route path="/comparador-procedimentos" element={
                            <ProtectedPage modulo="cadastros_tiss"><ComparadorProcedimentos handleLogout={handleLogout} /></ProtectedPage>
                        } />
                        <Route path="/auditoria/pivot" element={
                            <ProtectedPage modulo="cadastros_tiss"><PivotViewPage /></ProtectedPage>
                        } />
                        <Route path="/auditoria-sus" element={
                            <ProtectedPage modulo="cadastros_tiss"><AuditoriaSus handleLogout={handleLogout} /></ProtectedPage>
                        } />

                        {/* Configurações */}
                        <Route path="/configuracoes" element={
                            <ProtectedPage modulo="configuracoes"><PerfilUsuario handleLogout={handleLogout} /></ProtectedPage>
                        } />
                        {/* Admin */}
                        <Route path="/admin" element={
                            <ProtectedPage modulo="admin"><CadastroUsuario isUserAdminn={isAdmin} handleLogout={handleLogout} /></ProtectedPage>
                        } />

                        {/* Logs */}
                        <Route path="/logs" element={
                            <ProtectedPage modulo="logs"><LogViewer isUserAdminn={isAdmin} handleLogout={handleLogout} /></ProtectedPage>
                        } />

                        {/* Suporte */}
                        <Route path="/contato" element={
                            <ProtectedPage modulo="suporte"><Suporte handleLogout={handleLogout} /></ProtectedPage>
                        } />

                        {/* Utilitários */}
                        <Route path="/relatorio" element={<LoginOrPage><RelatorioComponente handleLogout={handleLogout} /></LoginOrPage>} />
                        <Route path="/preview-tabela" element={<LoginOrPage><TabelaPreview /></LoginOrPage>} />

                        {/* Fallback */}
                        <Route path="*" element={<LoginOrPage><Home handleLogout={handleLogout} /></LoginOrPage>} />
                    </Routes>
                </Container>
            </Box>
        </Box>
    );
}

export default App;
